/**
 * Password policy aligned with bright.blue-cloud
 * (`services/passwordPolicyService.js` → `getDefaultPasswordPolicyConfig`).
 * Keep in sync with `lib/passwordPolicy.ts` on the server.
 */
export const PASSWORD_MIN_LENGTH = 12;

export const PASSWORD_POLICY = {
  minLength: PASSWORD_MIN_LENGTH,
  regex: {
    pattern: `^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[^a-zA-Z0-9]).{${PASSWORD_MIN_LENGTH},}$`,
    description: `Password must be at least ${PASSWORD_MIN_LENGTH} characters long and include an uppercase letter, a lowercase letter, a number, and a special character.`,
    lengthCheck: 'At least 12 characters long.',
    uppercaseCheck: 'At least one uppercase letter (A-Z)',
    lowercaseCheck: 'At least one lowercase letter (a-z)',
    numberCheck: 'At least one number (0-9)',
    specialCharacterCheck: 'At least one special character (!@#$%^&*...)',
  },
} as const;

export interface PasswordRequirementStatus {
  key: 'length' | 'uppercase' | 'lowercase' | 'number' | 'special';
  label: string;
  isMet: boolean;
}

export function getPasswordRequirementStatuses(password: string): PasswordRequirementStatus[] {
  const value = typeof password === 'string' ? password : '';
  return [
    {
      key: 'length',
      label: PASSWORD_POLICY.regex.lengthCheck,
      isMet: value.length >= PASSWORD_POLICY.minLength,
    },
    {
      key: 'uppercase',
      label: PASSWORD_POLICY.regex.uppercaseCheck,
      isMet: /[A-Z]/.test(value),
    },
    {
      key: 'lowercase',
      label: PASSWORD_POLICY.regex.lowercaseCheck,
      isMet: /[a-z]/.test(value),
    },
    {
      key: 'number',
      label: PASSWORD_POLICY.regex.numberCheck,
      isMet: /\d/.test(value),
    },
    {
      key: 'special',
      label: PASSWORD_POLICY.regex.specialCharacterCheck,
      isMet: /[^a-zA-Z0-9]/.test(value),
    },
  ];
}

export function passwordMeetsPolicy(password: string): boolean {
  if (typeof password !== 'string') return false;
  return new RegExp(PASSWORD_POLICY.regex.pattern).test(password);
}

export function validatePasswordPolicy(password: string): {
  isValid: boolean;
  errors: string[];
} {
  if (typeof password !== 'string' || password.length === 0) {
    return { isValid: false, errors: ['Password is required'] };
  }

  const errors: string[] = [];
  if (password.length < PASSWORD_POLICY.minLength) {
    errors.push(`Password must be at least ${PASSWORD_POLICY.minLength} characters long`);
  }
  // Same as blue-cloud: length and pattern are checked independently.
  if (!new RegExp(PASSWORD_POLICY.regex.pattern).test(password)) {
    errors.push(PASSWORD_POLICY.regex.description);
  }

  return { isValid: errors.length === 0, errors };
}
